import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import {
  Alert,
  Button,
  IconButton,
  InputAdornment,
  LinearProgress,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { useQuery } from '@tanstack/react-query';
import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { DayliteFilterOperand } from 'shared/types/dayliteModels';
import { OpportunityQueryData, searchEvents } from '../../../loaders/eventsLoaders';
import { AppDispatch } from '../../../store';
import {
  clearAllEventsFilters,
  setAllEventsFilterModel,
  setAllEventsNameSearch,
  setAllEventsCategoryFilter,
  setAllEventsStartDate,
  setAllEventsEndDate,
  setAllEventsCurrentSearchState,
  setAllEventsHasPerformedInitialLoad
} from '../../../store/slices/reportControlsSlice';
import { RootState } from '../../../../shared/types/appTypes';

interface EventFilterControlsProps {
  onEventsLoaded: (events: OpportunityQueryData[]) => void;
  onSearchChange: (search: string) => void;
  onControlsChange?: (hasChanges: boolean) => void;
  hasControlsChanged?: boolean;
  disableAutoLoad?: boolean;
}

const EventFilterControls: React.FC<EventFilterControlsProps> = ({
  onEventsLoaded,
  onSearchChange,
  onControlsChange,
  hasControlsChanged = false,
  disableAutoLoad = false
}) => {
  const dispatch = useDispatch<AppDispatch>();

  // Get all state from Redux store with safe fallbacks
  const reportControls = useSelector((state: RootState) => state.reportControls);
  const {
    allEventsFilterModel: filterModel = { items: [], quickFilterValues: [] },
    allEventsNameSearch = '',
    allEventsCategoryFilter = 'events',
    allEventsStartDate = null,
    allEventsEndDate = null,
    allEventsCurrentSearchState = null,
    allEventsHasPerformedInitialLoad = false
  } = reportControls || {};

  // Local state only for errors
  const [filterError, setFilterError] = useState<string | null>(null);

  // State to track which search parameters to use for the query
  const [queryKey, setQueryKey] = useState<{
    startDate: string | null;
    endDate: string | null;
    category: 'events' | 'genavail';
  } | null>(null);

  // Helper to safely convert ISO string to Date
  const isoStringToDate = (isoString: string | null): Date | null => {
    if (!isoString) return null;
    try {
      const date = new Date(isoString);
      // Check if date is valid
      if (isNaN(date.getTime())) return null;
      return date;
    } catch {
      return null;
    }
  };

  // Convert ISO strings back to Date objects for the DatePicker components
  const startDate = isoStringToDate(allEventsStartDate);
  const endDate = isoStringToDate(allEventsEndDate);

  // Use TanStack Query for cached event searching
  const {
    data: queryData,
    isLoading: isQueryLoading,
    error: queryError,
    refetch
  } = useQuery({
    queryKey: ['events', queryKey?.startDate, queryKey?.endDate, queryKey?.category],
    queryFn: async () => {
      if (!queryKey) return null;

      // Format the dates for the Daylite API
      const filters: DayliteFilterOperand = {
        category:
          queryKey.category === 'events'
            ? { any: ['BOOKED', 'SALES', 'DOUBLES', 'BOOKED | TRAVEL'] }
            : { any: ['GENERAL AVAIL'] }
      };

      // Handle date range filtering
      const startDateObj = queryKey.startDate ? new Date(queryKey.startDate) : null;
      const endDateObj = queryKey.endDate ? new Date(queryKey.endDate) : null;

      if (startDateObj && endDateObj) {
        const formattedStartDate = startDateObj.toISOString().split('T')[0];
        const formattedEndDate = endDateObj.toISOString().split('T')[0];
        filters.forecasted = {
          in_range: [formattedStartDate, formattedEndDate]
        };
      } else if (startDateObj) {
        const formattedStartDate = startDateObj.toISOString().split('T')[0];
        filters.forecasted = { greater_than_equal: formattedStartDate };
      } else if (endDateObj) {
        const formattedEndDate = endDateObj.toISOString().split('T')[0];
        filters.forecasted = { less_than_equal: formattedEndDate };
      }

      // Execute search
      const response = await searchEvents({
        limit: 250,
        filters: filters,
        sortBy: 'forecasted',
        sortDirection: 'asc'
      });

      if (!response.success || !response.data) {
        throw new Error(response.message || 'Failed to filter events');
      }

      const results = Array.isArray(response.data.results) ? response.data.results : [];
      return results;
    },
    enabled: queryKey !== null, // Only run query when we have query parameters
    staleTime: 5 * 60 * 1000, // 5 minutes - data stays fresh
    gcTime: 10 * 60 * 1000 // 10 minutes - cached data kept in memory
  });

  // Update parent when query data changes
  useEffect(() => {
    if (queryData) {
      onEventsLoaded(queryData);

      // Mark that we've performed the initial load
      if (!allEventsHasPerformedInitialLoad) {
        dispatch(setAllEventsHasPerformedInitialLoad(true));
      }
    }
  }, [queryData, onEventsLoaded, allEventsHasPerformedInitialLoad, dispatch]);

  // Update filter error from query error
  useEffect(() => {
    if (queryError) {
      setFilterError('Failed to filter events. Please try again.');
    }
  }, [queryError]);

  // Function to check if current values differ from the last searched state
  const checkForChanges = useCallback(() => {
    if (!allEventsHasPerformedInitialLoad || !allEventsCurrentSearchState) return false;

    const currentStartDate = allEventsStartDate ? new Date(allEventsStartDate).getTime() : null;
    const currentEndDate = allEventsEndDate ? new Date(allEventsEndDate).getTime() : null;
    const searchStateStartDate = allEventsCurrentSearchState?.startDate
      ? new Date(allEventsCurrentSearchState.startDate).getTime()
      : null;
    const searchStateEndDate = allEventsCurrentSearchState?.endDate
      ? new Date(allEventsCurrentSearchState.endDate).getTime()
      : null;

    const startDateChanged = currentStartDate !== searchStateStartDate;
    const endDateChanged = currentEndDate !== searchStateEndDate;
    const categoryChanged = allEventsCategoryFilter !== allEventsCurrentSearchState?.category;
    const searchChanged = allEventsNameSearch.trim() !== (allEventsCurrentSearchState?.nameSearch || '');

    return startDateChanged || endDateChanged || categoryChanged || searchChanged;
  }, [
    allEventsStartDate,
    allEventsEndDate,
    allEventsCategoryFilter,
    allEventsNameSearch,
    allEventsHasPerformedInitialLoad,
    allEventsCurrentSearchState
  ]);

  // Notify parent when controls change
  useEffect(() => {
    const hasChanges = checkForChanges();
    onControlsChange?.(hasChanges);
  }, [checkForChanges, onControlsChange]);

  // On mount, trigger query with currentSearchState if it exists, otherwise with defaults
  useEffect(() => {
    if (!disableAutoLoad && queryKey === null) {
      const hasPerformedSearch = allEventsCurrentSearchState?.startDate !== null;

      const timer = setTimeout(() => {
        if (hasPerformedSearch) {
          // Re-execute the last search using currentSearchState
          // This will use cached data if available
          setQueryKey({
            startDate: allEventsCurrentSearchState?.startDate || null,
            endDate: allEventsCurrentSearchState?.endDate || null,
            category: allEventsCurrentSearchState?.category || 'events'
          });
        } else {
          // First time - search with current default dates
          setQueryKey({
            startDate: allEventsStartDate,
            endDate: allEventsEndDate,
            category: allEventsCategoryFilter
          });

          // Update current search state immediately
          dispatch(
            setAllEventsCurrentSearchState({
              startDate: allEventsStartDate,
              endDate: allEventsEndDate,
              category: allEventsCategoryFilter,
              nameSearch: allEventsNameSearch.trim()
            })
          );
        }
      }, 100);

      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  const handleNameSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    dispatch(setAllEventsNameSearch(value));
    onSearchChange(value);

    const newFilterModel = {
      ...(filterModel || {}),
      quickFilterValues: value ? [value] : []
    };
    dispatch(setAllEventsFilterModel(newFilterModel));
  };

  const handleCategoryChange = (event: React.MouseEvent<HTMLElement>, newCategory: 'events' | 'genavail') => {
    if (newCategory !== null) {
      dispatch(setAllEventsCategoryFilter(newCategory));
    }
  };

  const handleFindClick = () => {
    setFilterError(null);

    // Trigger new query with current input values
    setQueryKey({
      startDate: allEventsStartDate,
      endDate: allEventsEndDate,
      category: allEventsCategoryFilter
    });

    // Update the current search state to reflect what was just searched
    dispatch(
      setAllEventsCurrentSearchState({
        startDate: allEventsStartDate,
        endDate: allEventsEndDate,
        category: allEventsCategoryFilter,
        nameSearch: allEventsNameSearch.trim()
      })
    );

    // Reset the changes state after performing search
    onControlsChange?.(false);
  };

  const handleClearNameSearch = () => {
    dispatch(setAllEventsNameSearch(''));
    onSearchChange('');

    const newFilterModel = {
      ...(filterModel || {}),
      quickFilterValues: []
    };
    dispatch(setAllEventsFilterModel(newFilterModel));
  };

  const handleResetFilter = () => {
    // Reset to default values using the clearAllEventsFilters action
    setFilterError(null);
    onSearchChange('');
    dispatch(clearAllEventsFilters());

    // Reset the query key to null so it will re-initialize on next mount
    setQueryKey(null);

    // Reset the changes state
    onControlsChange?.(false);
  };

  // Helper to safely convert Date to ISO string
  const dateToISOString = (date: Date | null): string | null => {
    if (!date) return null;
    try {
      // Check if date is valid
      if (isNaN(date.getTime())) return null;
      return date.toISOString();
    } catch {
      return null;
    }
  };

  return (
    <>
      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DatePicker
            label="Events From"
            value={startDate}
            onChange={(newValue) => dispatch(setAllEventsStartDate(dateToISOString(newValue)))}
            slotProps={{
              textField: {
                style: { minWidth: '200px' },
                onKeyDown: (e) => {
                  if (e.key === 'Enter' && hasControlsChanged) {
                    handleFindClick();
                  }
                }
              }
            }}
          />
          <DatePicker
            label="To"
            value={endDate}
            onChange={(newValue) => dispatch(setAllEventsEndDate(dateToISOString(newValue)))}
            slotProps={{
              textField: {
                style: { minWidth: '200px' },
                onKeyDown: (e) => {
                  if (e.key === 'Enter' && hasControlsChanged) {
                    handleFindClick();
                  }
                }
              }
            }}
          />
        </LocalizationProvider>

        {/* Category Toggle Button Group */}
        <ToggleButtonGroup
          value={allEventsCategoryFilter}
          exclusive
          onChange={handleCategoryChange}
          aria-label="category filter"
        >
          <ToggleButton value="events" aria-label="events">
            Events
          </ToggleButton>
          <ToggleButton value="genavail" aria-label="general availability">
            Gen Avail
          </ToggleButton>
        </ToggleButtonGroup>

        {/* Find button for triggering the filter */}
        <Button
          variant="contained"
          onClick={handleFindClick}
          disabled={isQueryLoading || (!hasControlsChanged && allEventsHasPerformedInitialLoad)}
          sx={{
            opacity: !hasControlsChanged && allEventsHasPerformedInitialLoad ? 0.5 : 1
          }}
        >
          Find
        </Button>

        <Button variant="outlined" onClick={handleResetFilter} disabled={isQueryLoading}>
          Reset
        </Button>

        {/* Name search field */}
        <TextField
          variant="outlined"
          size="small"
          value={allEventsNameSearch}
          onChange={handleNameSearchChange}
          sx={{ flex: 1, minWidth: '200px' }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: allEventsNameSearch ? (
              <InputAdornment position="end">
                <IconButton aria-label="clear search" onClick={handleClearNameSearch} edge="end" size="small">
                  <ClearIcon />
                </IconButton>
              </InputAdornment>
            ) : null
          }}
        />
      </Stack>

      {filterError && <Alert severity="error">{filterError}</Alert>}
    </>
  );
};

export default EventFilterControls;
